import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api, { refreshSession, setOnUnauthorized } from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

const decodeSessionIdFromToken = (token) => {
  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return null;

    const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const payload = JSON.parse(atob(paddedBase64));
    return payload?.sessionId || null;
  } catch (_error) {
    return null;
  }
};


const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [supervisorSelect, setSupervisorSelect] = useState(null);
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());

  // Registrar callback de 401 no api.js (interceptors vivem lá, no nível do módulo)
  useEffect(() => {
    setOnUnauthorized(() => {
      setIsAuth(false);
      if (history.location.pathname !== "/login") {
        history.push("/login");
      }
    });
    return () => setOnUnauthorized(null);
  }, [history]);

  // Effect para inicialização do token
  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          const { token: refreshedToken, user: refreshedUser } = await refreshSession({ includeUser: true });
          const sessionId = decodeSessionIdFromToken(refreshedToken);
          if (sessionId) {
            localStorage.setItem("sessionId", sessionId);
          }
          setIsAuth(true);
          setUser(refreshedUser || {});
        } catch (err) {
          toastError(err);
        }
      }
      setLoading(false);
    })();
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (user?.profile === "manager") {
      if (socket?.disconnect) socket.disconnect();
      setSocket(null);
      return undefined;
    }
    if (Object.keys(user).length && user.id > 0 && user.companyId) {
      console.log("Configurando socket para user", user.id, "company", user.companyId);
      
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexão socket
      const socketInstance = socketConnection({ user: {
        companyId: user.companyId,
        id: user.id }
      });
      
      if (socketInstance) {
        setSocket(socketInstance);

        // Aguardar um pouco para garantir que o socket está configurado
        setTimeout(() => {
          const eventName = `company-${user.companyId}-user`;
          const forceLogoutEvent = `company-${user.companyId}-force-logout`;
          
          const handleUserUpdate = (data) => {
            if (data.action === "update" && data.user.id === user.id) {
              setUser(prev => ({ ...prev, ...data.user }));
            }
          };

          const handleForceLogout = (data) => {
            const localSessionId = localStorage.getItem("sessionId");

            if (
              data.userId === user.id &&
              data.targetSessionId &&
              localSessionId &&
              data.targetSessionId === localSessionId
            ) {
              toast.warn(i18n.t("auth.toasts.sessionExpired") || "Sua sessão foi encerrada porque você acessou de outro dispositivo.", { autoClose: 6000 });
              // Executar logout após breve delay para o toast aparecer
              setTimeout(async () => {
                try {
                  if (socketInstance && typeof socketInstance.disconnect === 'function') {
                    socketInstance.disconnect();
                  }
                  await api.delete("/auth/logout").catch(() => {});
                  setIsAuth(false);
                  setUser({});
                  setSocket(null);
                  localStorage.removeItem("token");
                  localStorage.removeItem("cshow");
                  localStorage.removeItem("sessionId");
                  api.defaults.headers.Authorization = undefined;
                  history.push("/login");
                } catch (_) {
                  setIsAuth(false);
                  setUser({});
                  setSocket(null);
                  localStorage.removeItem("token");
                  localStorage.removeItem("sessionId");
                  api.defaults.headers.Authorization = undefined;
                  history.push("/login");
                }
              }, 1500);
            }
          };

          // Verificar se o socket tem o método 'on'
          if (socketInstance && typeof socketInstance.on === 'function') {
            socketInstance.on(eventName, handleUserUpdate);
            socketInstance.on(forceLogoutEvent, handleForceLogout);
            listenersRef.current.add(eventName);
            listenersRef.current.add(forceLogoutEvent);
            console.log(`Listener adicionado para: ${eventName}`);
          } else {
            console.error("Socket instance não tem método 'on'", socketInstance);
          }
        }, 100);
      }
    }

    // Cleanup function
    return () => {
      if (socket && listenersRef.current.size > 0) {
        console.log("Limpando listeners do socket para user", user.id);
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId, user.profile]); // Dependências específicas

  // Effect para buscar dados do usuário atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
      } catch (err) {
        console.log("Erro ao buscar usuário atual:", err);
      }
    };
    
    if (isAuth) {
      fetchCurrentUser();
    }
  }, [isAuth]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);

      // Afiliado: painel proprio, sem empresa
      if (data.user && data.user.isAffiliate) {
        const sessionId = decodeSessionIdFromToken(data.token);
        localStorage.setItem("token", JSON.stringify(data.token));
        if (sessionId) localStorage.setItem("sessionId", sessionId);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        history.push("/");
        setLoading(false);
        return;
      }

      // Supervisor: escolher empresa antes de entrar
      if (data.supervisorSelect) {
        localStorage.setItem("supSelectToken", data.token);
        setSupervisorSelect({ companies: data.companies || [] });
        setLoading(false);
        return;
      }
      if (data.user?.profile === "manager") {
        const sessionId = decodeSessionIdFromToken(data.token);
        localStorage.setItem("token", JSON.stringify(data.token));
        if (sessionId) localStorage.setItem("sessionId", sessionId);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);
        history.push("/management");
        setLoading(false);
        return;
      }
      const {
        user: { company },
      } = data;

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
      const hojeInicio = moment().startOf('day');
      const vencimentoInicio = moment(dueDate).startOf('day');
      
      var diff = vencimentoInicio.diff(hojeInicio, 'days');
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, 'day');
      var dias = diff;

      if (before === true) {
        const sessionId = decodeSessionIdFromToken(data.token);
        localStorage.setItem("token", JSON.stringify(data.token));
        if (sessionId) {
          localStorage.setItem("sessionId", sessionId);
        }
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        
        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      localStorage.removeItem("sessionId");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const enterSupervisorCompany = async (companyId) => {
    setLoading(true);
    try {
      const selToken = localStorage.getItem("supSelectToken");
      const { data } = await api.post("/supervisor/enter", { companyId }, { headers: { Authorization: `Bearer ${selToken}` } });
      const sessionId = decodeSessionIdFromToken(data.token);
      localStorage.setItem("token", JSON.stringify(data.token));
      if (sessionId) localStorage.setItem("sessionId", sessionId);
      localStorage.removeItem("supSelectToken");
      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      setUser(data.user);
      setIsAuth(true);
      setSupervisorSelect(null);
      toast.success(i18n.t("auth.toasts.success"));
      history.push("/tickets");
      setLoading(false);
    } catch (err) { toastError(err); setLoading(false); }
  };

  const switchSupervisorCompany = async (companyId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/supervisor/switch", { companyId });
      localStorage.setItem("token", JSON.stringify(data.token));
      const sessionId = decodeSessionIdFromToken(data.token);
      if (sessionId) localStorage.setItem("sessionId", sessionId);
      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      setUser(data.user);
      setIsAuth(true);
      toast.success("Empresa alterada!");
      window.location.reload();
    } catch (err) { toastError(err); setLoading(false); }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    supervisorSelect,
    enterSupervisorCompany,
    switchSupervisorCompany,
    socket,
  };
};

export default useAuth;
